import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'

export interface PlayerTrack {
  // Stable identity for "is this the track that's playing" -- see tracks.ts.
  key: string
  title: string
  artist: string
  imageUrl?: string | null
  // Where to ask for the preview (a catalog song, or an artist + title lookup).
  previewPath: string
}

export type PlayerStatus = 'idle' | 'loading' | 'playing' | 'paused' | 'unavailable'

interface PreviewResponse {
  // 'pending' = the server is looking it up in the background; ask again.
  status: 'found' | 'unavailable' | 'pending'
  url?: string
  source?: 'itunes' | 'deezer'
  kind?: 'exact' | 'similar'
  matched?: { artist: string; title: string }
}

export interface PlayerQueue {
  tracks: PlayerTrack[]
  index: number
}

export interface PlayerState {
  track: PlayerTrack | null
  status: PlayerStatus
  // Set while playing through a list ("audition the ride"): the preview
  // that ends hands over to the next one.
  queue?: PlayerQueue | null
  source?: 'itunes' | 'deezer'
  // "similar" = same song, different version (live cut, remix, extra artists)
  kind?: 'exact' | 'similar'
  matched?: { artist: string; title: string }
}

interface PlayerActions {
  play: (track: PlayerTrack) => void
  playQueue: (tracks: PlayerTrack[], startIndex: number) => void
  next: () => void
  prev: () => void
  toggle: () => void
  seek: (fraction: number) => void
  close: () => void
}

interface PlayerProgress {
  currentTime: number
  duration: number
}

// Asks for a preview, polling while the server says "pending". Resolves to
// null if it never got an answer in time, or `isStale()` says the person has
// moved on to something else (so a superseded lookup stops asking).
async function fetchPreview(path: string, isStale: () => boolean): Promise<PreviewResponse | null> {
  const deadline = Date.now() + MAX_LOOKUP_WAIT_MS

  for (;;) {
    const response = await fetch(path, { headers: { Accept: 'application/json' } })
    if (!response.ok) throw new Error(`Request failed: ${response.status}`)

    const data = (await response.json()) as PreviewResponse
    if (data.status !== 'pending') return data
    if (isStale() || Date.now() >= deadline) return null

    await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS))
  }
}

const IDLE: PlayerState = { track: null, status: 'idle' }

// Safe defaults so components render (and tests run) without a provider.
const StateContext = createContext<PlayerState>(IDLE)
const ActionsContext = createContext<PlayerActions>({ play() {}, playQueue() {}, next() {}, prev() {}, toggle() {}, seek() {}, close() {} })
const ProgressContext = createContext<PlayerProgress>({ currentTime: 0, duration: 0 })

export const usePlayerState = () => useContext(StateContext)
export const usePlayerActions = () => useContext(ActionsContext)
export const usePlayerProgress = () => useContext(ProgressContext)

// A first-ever lookup for a song happens in a background job (iTunes is
// rate-limited on purpose, so it takes a few seconds), and the server answers
// "pending" until it's done -- so we ask again on this cadence, and give up
// after MAX_LOOKUP_WAIT_MS rather than spin forever.
const POLL_INTERVAL_MS = 1000
const MAX_LOOKUP_WAIT_MS = 45_000

const UNAVAILABLE_DISMISS_MS = 4000
// In a queue, a track with no preview is skipped past quickly rather than stalling the ride.
const UNAVAILABLE_SKIP_MS = 1500

// One <audio> element for the whole app, living above the router's <Outlet/>
// so a preview keeps playing while you navigate. Split into three contexts
// so the many play buttons only re-render on state changes -- not on every
// timeupdate tick, which only the player bar's progress cares about.
export function PlayerProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<PlayerState>(IDLE)
  const [progress, setProgress] = useState<PlayerProgress>({ currentTime: 0, duration: 0 })
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const stateRef = useRef(state)
  const requestId = useRef(0) // discards a lookup that finishes after you clicked something else
  const advanceRef = useRef<(delta: number) => boolean>(() => false)
  stateRef.current = state

  const getAudio = useCallback(() => {
    if (!audioRef.current) audioRef.current = new Audio()
    return audioRef.current
  }, [])

  useEffect(() => {
    const audio = getAudio()
    const onTime = () => setProgress({ currentTime: audio.currentTime, duration: audio.duration || 0 })
    const onPlaying = () => setState((s) => (s.track ? { ...s, status: 'playing' } : s))
    const onPause = () => setState((s) => (s.track && s.status === 'playing' ? { ...s, status: 'paused' } : s))
    const onEnded = () => {
      audio.currentTime = 0
      setState((s) => (s.track ? { ...s, status: 'paused' } : s))
      setProgress((p) => ({ ...p, currentTime: 0 }))
      advanceRef.current(1) // no-op unless we're playing through a queue
    }
    const onError = () => setState((s) => (s.track ? { ...s, status: 'unavailable' } : s))

    audio.addEventListener('timeupdate', onTime)
    audio.addEventListener('loadedmetadata', onTime)
    audio.addEventListener('playing', onPlaying)
    audio.addEventListener('pause', onPause)
    audio.addEventListener('ended', onEnded)
    audio.addEventListener('error', onError)
    return () => {
      audio.pause()
      audio.removeEventListener('timeupdate', onTime)
      audio.removeEventListener('loadedmetadata', onTime)
      audio.removeEventListener('playing', onPlaying)
      audio.removeEventListener('pause', onPause)
      audio.removeEventListener('ended', onEnded)
      audio.removeEventListener('error', onError)
    }
  }, [getAudio])

  const close = useCallback(() => {
    requestId.current += 1
    const audio = getAudio()
    audio.pause()
    audio.removeAttribute('src')
    audio.load()
    setState(IDLE)
    setProgress({ currentTime: 0, duration: 0 })
  }, [getAudio])

  const toggle = useCallback(() => {
    const audio = getAudio()
    if (audio.paused) void audio.play().catch(() => {})
    else audio.pause()
  }, [getAudio])

  // Start a track fresh (looking its preview up first). `queue` is carried so
  // the list survives every state change on the way to playing.
  const startTrack = useCallback(
    (track: PlayerTrack, queue: PlayerQueue | null) => {
      const id = ++requestId.current
      const audio = getAudio()
      audio.pause()
      setState({ track, status: 'loading', queue })
      setProgress({ currentTime: 0, duration: 0 })

      fetchPreview(track.previewPath, () => id !== requestId.current)
        .then(async (data) => {
          if (id !== requestId.current) return
          if (!data || data.status !== 'found' || !data.url) {
            setState({ track, status: 'unavailable', queue })
            return
          }
          setState({ track, status: 'loading', source: data.source, kind: data.kind, matched: data.matched, queue })
          audio.src = data.url
          await audio.play()
        })
        .catch(() => {
          if (id === requestId.current) setState({ track, status: 'unavailable', queue })
        })
    },
    [getAudio],
  )

  const isCurrent = (track: PlayerTrack) => {
    const current = stateRef.current
    return current.track?.key === track.key && (current.status === 'playing' || current.status === 'paused')
  }

  const play = useCallback(
    (track: PlayerTrack) => {
      // Same song: the button is a play/pause toggle, not a restart.
      if (isCurrent(track)) toggle()
      else startTrack(track, null)
    },
    [startTrack, toggle],
  )

  const playQueue = useCallback(
    (tracks: PlayerTrack[], startIndex: number) => {
      const track = tracks[startIndex]
      if (!track) return
      const queue = { tracks, index: startIndex }
      if (isCurrent(track)) {
        setState((s) => ({ ...s, queue })) // keep the (possibly re-ordered) list, just toggle
        toggle()
      } else {
        startTrack(track, queue)
      }
    },
    [startTrack, toggle],
  )

  // Move within the queue; returns whether there was somewhere to go.
  const advance = useCallback(
    (delta: number) => {
      const queue = stateRef.current.queue
      if (!queue) return false
      const index = queue.index + delta
      if (index < 0 || index >= queue.tracks.length) return false
      startTrack(queue.tracks[index], { tracks: queue.tracks, index })
      return true
    },
    [startTrack],
  )
  advanceRef.current = advance

  const next = useCallback(() => {
    advance(1)
  }, [advance])

  // Like a media player: past the first few seconds it restarts the track.
  const prev = useCallback(() => {
    const audio = getAudio()
    if (audio.currentTime > 3 || !advance(-1)) audio.currentTime = 0
  }, [advance, getAudio])

  // "No preview" is a moment, not a state to sit in -- and inside a queue it
  // shouldn't stall the whole ride: skip on to the next track.
  useEffect(() => {
    if (state.status !== 'unavailable') return
    const hasNext = Boolean(state.queue && state.queue.index < state.queue.tracks.length - 1)
    const timer = setTimeout(() => (hasNext ? advance(1) : close()), hasNext ? UNAVAILABLE_SKIP_MS : UNAVAILABLE_DISMISS_MS)
    return () => clearTimeout(timer)
  }, [state.status, state.track, state.queue, advance, close])

  const seek = useCallback(
    (fraction: number) => {
      const audio = getAudio()
      if (audio.duration) audio.currentTime = Math.min(Math.max(fraction, 0), 1) * audio.duration
    },
    [getAudio],
  )

  const actions = useMemo(() => ({ play, playQueue, next, prev, toggle, seek, close }), [play, playQueue, next, prev, toggle, seek, close])

  return (
    <ActionsContext.Provider value={actions}>
      <StateContext.Provider value={state}>
        <ProgressContext.Provider value={progress}>{children}</ProgressContext.Provider>
      </StateContext.Provider>
    </ActionsContext.Provider>
  )
}
